import { cva } from "class-variance-authority";
import { CircleCheck, CircleX, Info, TriangleAlert } from "lucide-react";
import { cloneElement } from "react";
import { cn } from "../../utils";
import { Button } from "../Button/Button";
import { Text } from "../Typography";
import { BannerProps } from "./types";

const bannerButtonVariants = cva("", {
  variants: {
    color: {
      neutral: "",
      info: "",
      success: "",
      warning: "",
      danger: "",
    },
    variant: {
      outlined: "",
      ghost: "",
    },
  },
  compoundVariants: [
    {
      variant: "outlined",
      color: "neutral",
      className:
        "border-neutral-500 text-neutral-500 hover:bg-neutral-500/5 hover:border-neutral-600 focus-visible:bg-neutral-500/5 focus-visible:border-neutral-600 focus-visible:outline-neutral-500 active:bg-neutral-500/10",
    },
    {
      variant: "outlined",
      color: "info",
      className:
        "border-info-800 text-info-800 hover:bg-info-800/5 hover:border-info-900 focus-visible:bg-info-800/5 focus-visible:border-info-900 focus-visible:outline-info-800 active:bg-info-800/10",
    },
    {
      variant: "outlined",
      color: "success",
      className:
        "border-success-800 text-success-800 hover:bg-success-800/5 hover:border-success-900 focus-visible:bg-success-800/5 focus-visible:border-success-900 focus-visible:outline-success-800 active:bg-success-800/10",
    },
    {
      variant: "outlined",
      color: "warning",
      className:
        "border-warning-900 text-warning-900 hover:bg-warning-900/5 hover:border-warning-900 focus-visible:bg-warning-900/5 focus-visible:border-warning-900 focus-visible:outline-warning-900 active:bg-warning-900/10",
    },
    {
      variant: "outlined",
      color: "danger",
      className:
        "border-danger-900 text-danger-900 hover:bg-danger-900/5 hover:border-danger-900 focus-visible:bg-danger-900/5 focus-visible:border-danger-900 focus-visible:outline-danger-900 active:bg-danger-900/10",
    },
    {
      variant: "ghost",
      color: "neutral",
      className:
        "border-transparent text-neutral-500 hover:bg-neutral-500/5 focus-visible:bg-neutral-500/5 focus-visible:outline-neutral-500 active:bg-neutral-500/10",
    },
    {
      variant: "ghost",
      color: "info",
      className:
        "border-transparent text-info-800 hover:bg-info-800/5 focus-visible:bg-info-800/5 focus-visible:outline-info-800 active:bg-info-800/10",
    },
    {
      variant: "ghost",
      color: "success",
      className:
        "border-transparent text-success-800 hover:bg-success-800/5 focus-visible:bg-success-800/5 focus-visible:outline-success-800 active:bg-success-800/10",
    },
    {
      variant: "ghost",
      color: "warning",
      className:
        "border-transparent text-warning-900 hover:bg-warning-900/5 focus-visible:bg-warning-900/5 focus-visible:outline-warning-900 active:bg-warning-900/10",
    },
    {
      variant: "ghost",
      color: "danger",
      className:
        "border-transparent text-danger-900 hover:bg-danger-900/5 focus-visible:bg-danger-900/5 focus-visible:outline-danger-900 active:bg-danger-900/10",
    },
  ],
  defaultVariants: {
    color: "neutral",
    variant: "outlined",
  },
});

const bannerVariants = cva("flex gap-3", {
  variants: {
    variant: {
      inline: "p-0",
      card: "p-4 rounded-md",
      bleed: "p-4",
    },
    color: {
      neutral: "text-neutral-500",
      info: "text-info-800",
      success: "text-success-800",
      warning: "text-warning-900",
      danger: "text-danger-900",
    },
    layout: {
      inline: "items-center",
      stacked: "items-start",
    },
  },
  compoundVariants: [
    {
      variant: "card",
      color: "neutral",
      className: "border border-neutral-500/10 bg-neutral-100",
    },
    {
      variant: "card",
      color: "info",
      className: "border border-info-200 bg-info-50",
    },
    {
      variant: "card",
      color: "success",
      className: "border border-success-200 bg-success-50",
    },
    {
      variant: "card",
      color: "danger",
      className: "border border-danger-200 bg-danger-50",
    },
    {
      variant: "card",
      color: "warning",
      className: "border border-warning-200 bg-warning-50",
    },
    {
      variant: "bleed",
      color: "neutral",
      className: "border-b border-neutral-500/10 bg-neutral-100",
    },
    {
      variant: "bleed",
      color: "info",
      className: "border-b border-info-200 bg-info-50",
    },
    {
      variant: "bleed",
      color: "success",
      className: "border-b border-success-200 bg-success-50",
    },
    {
      variant: "bleed",
      color: "danger",
      className: "border-b border-danger-200 bg-danger-50",
    },
    {
      variant: "bleed",
      color: "warning",
      className: "border-b border-warning-200 bg-warning-50",
    },
  ],
  defaultVariants: {
    variant: "card",
    color: "neutral",
    layout: "inline",
  },
});

export const Banner = ({
  variant = "card",
  color = "neutral",
  layout = "inline",
  icon,
  title,
  text,
  actions,
  role,
  className,
  ...props
}: BannerProps) => {
  const defaultRole =
    color === "danger" ? "alert" : color === "warning" ? "alert" : "status";

  const bannerRole = role ?? defaultRole;

  const getDefaultIcon = () => {
    // @ts-ignore
    if (icon !== undefined) return cloneElement(icon, { size: 18 });

    switch (color) {
      case "success":
        return <CircleCheck size={18} />;
      case "danger":
        return <CircleX size={18} />;
      case "warning":
        return <TriangleAlert size={18} />;
      default:
        return <Info size={18} />;
    }
  };

  const actionButtons = Array.isArray(actions)
    ? actions
    : actions
      ? [actions]
      : [];

  return (
    <div
      role={bannerRole === "none" ? undefined : bannerRole}
      className={cn(bannerVariants({ variant, color, layout }), className)}
      {...props}
    >
      <div className={cn(layout === "stacked" && "mt-0.5")} aria-hidden="true">
        {getDefaultIcon()}
      </div>
      <div
        className={cn(
          "flex-1",
          layout === "stacked" ? "flex flex-col" : "flex items-center gap-2",
        )}
      >
        {title && (
          <Text size="md" weight="medium">
            {title}
          </Text>
        )}
        {text && <Text size="md">{text}</Text>}
        {actionButtons.length > 0 && layout === "stacked" && (
          <div className="flex gap-2 mt-3">
            {actionButtons.map((actionProps, index) => (
              <Button
                key={index}
                {...actionProps}
                variant={actionProps.variant || "outlined"}
                size="sm"
                className={cn(
                  bannerButtonVariants({
                    color,
                    variant: actionProps.variant || "outlined",
                  }),
                  actionProps.className,
                )}
              >
                {actionProps.content || actionProps.children}
              </Button>
            ))}
          </div>
        )}
      </div>
      {actionButtons.length > 0 && layout !== "stacked" && (
        <div className="flex gap-2 ms-auto">
          {actionButtons.map((actionProps, index) => (
            <Button
              key={index}
              {...actionProps}
              variant={actionProps.variant || "outlined"}
              size="sm"
              className={cn(
                bannerButtonVariants({
                  color,
                  variant: actionProps.variant || "outlined",
                }),
                actionProps.className,
              )}
            >
              {actionProps.content || actionProps.children}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};
